import { Button, Divider, Modal, notification, Space } from 'antd';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { ColumnsType } from 'antd/lib/table';
import { useHistory } from 'react-router-dom';
import {
  AppPageHeader,
  DeleteIcon,
  EditIcon,
  InfiniteTable,
  TabContentWrapper,
} from '@/components/_common';
import {
  useListQuizzes,
  useRemoveQuizzes,
} from '@/core/domain/crm/Quizzes/Quizzes.hooks';
import { Quiz } from '@/core/domain/crm/Quizzes/Quizzes.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { queryClient } from '@/Root';
import { getTableScrollX } from '@/utils/table';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { useDebounce } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const SearchQuestionnairePage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<{ description: string }>({
    description: '',
  });

  const history = useHistory();

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useListQuizzes(queryDebounce);

  const quizzes = flatInfiniteData(data);

  const removeQuizzes = useRemoveQuizzes();

  const handleRemoveClick = (quiz: Quiz) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'searchQuestionnaire.remove' }),
      content: `${intl.formatMessage({
        id: 'searchQuestionnaire.remove.alert',
      })} ${quiz.description}`,
      onOk: async () => {
        await removeQuizzes.mutateAsync(quiz.id);

        await queryClient.refetchQueries('quizzes');

        notification.success({
          message: intl.formatMessage({
            id: 'searchQuestionnaire.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'searchQuestionnaire.remove.notification.successDescription',
          }),
        });
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<Quiz> = [
    {
      key: 'description',
      title: intl.formatMessage({ id: 'searchQuestionnaire.list.description' }),
      dataIndex: 'description',
      width: 300,
      ellipsis: true,
    },
    {
      key: 'numberOfQuestions',
      title: intl.formatMessage({
        id: 'searchQuestionnaire.list.numberOfQuestions',
      }),
      dataIndex: 'numberOfQuestions',
      width: 150,
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="searchQuestionnaire.list.action" />,
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/customers/crm/searchQuestionnaire/${record.id}`}
            disabled={!hasPermissions([Authorities.CRM_QUESTION_UPDATE])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={!hasPermissions([Authorities.CRM_QUESTION_DELETE])}
          />
        </Space>
      ),
      ellipsis: true,
      width: 90,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <Button
            type="primary"
            block
            onClick={() =>
              history.push('/app/customers/crm/searchQuestionnaire/create')
            }
            disabled={!hasPermissions([Authorities.CRM_QUESTION_CREATE])}
          >
            {intl.formatMessage({
              id: 'searchQuestionnaire.list.button.newQuestionnaire',
            })}
          </Button>
        }
      >
        <AppPageHeader
          onSearch={(value) => setQueryParams({ description: value })}
        />

        <InfiniteTable<Quiz>
          size="middle"
          loading={isFetching}
          rowKey={(row) => String(row.id)}
          dataSource={quizzes}
          columns={columns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>
    </>
  );
};

export default SearchQuestionnairePage;
